import React, { useEffect } from "react";
import { ChevronLeft } from "lucide-react";
import { LoadingSkeleton } from "./components/skeleton";
import ConfirmArea from "./components/ConfirmArea";
import { Button } from "../button";
import Modal from "@/components/custom-modal";
import { useModal } from "@/hook/modal";
import TotalIsZero from "../total-is-zero";
import { IconButton } from "@/components/icon-button";

export interface AreaSelectionModalProps {
  open: boolean;
  onClose?: (value: any) => void;
  agencyArea?: any;
  handleRadioOnClick?: (item: any) => void;
  chipData: any[];
  setChipData?: any;
  stepCounter: number;
  setStepCounter?: any;
  areaDetails?: any[];
  areaDetailsLoading: boolean;
  agencyAreaLoading: boolean;
  handleCoverageAreaId?: (item: any) => void;
  selectedRadioId: any;
  setSelectedRadioId?: any;
  handleBack?: (from: string) => void;
  typeModal: "inside" | "outside";
  pathData: any[];
  setPathData?: any;
  agencyAreaAdd?: boolean;
  // add the missing prop expected by parent
  checkRadioRequestToSendarea_id?: boolean;
}

export default function AreaSelectionModal({
  open,
  onClose,
  agencyArea,
  handleRadioOnClick,
  setChipData,
  stepCounter,
  setStepCounter,
  areaDetails,
  areaDetailsLoading,
  agencyAreaLoading,
  handleCoverageAreaId,
  selectedRadioId,
  setSelectedRadioId,
  handleBack,
  typeModal,
  pathData,
  setPathData,
}: Readonly<AreaSelectionModalProps>) {
  const uniqueLevelName = areaDetails?.[0]?.level_name ?? "";

  const {
    isModalOpen: areaModalOpen,
    handleOpen: handleAreaModalOpen,
    handleClose: handleAreaModalClose,
  } = useModal();

  useEffect(() => {
    setChipData?.([]);
    setPathData?.([]);
    setStepCounter?.(0);
    setSelectedRadioId?.(null);
    localStorage.setItem("typeModal", typeModal);
  }, [typeModal, setChipData, setPathData, setStepCounter, setSelectedRadioId]);

  const handleClose = () => onClose?.(null);

  const renderTreeViewFirstStep = () => (
    <div className="flex flex-col gap-1">
      <h3 className="text-lg font-bold mb-3">مناطق تحت پوشش</h3>

      {agencyAreaLoading && <LoadingSkeleton />}

      {!agencyAreaLoading && typeModal === "inside" && (
        <TotalIsZero query={agencyArea?.data?.length} />
      )}

      {(typeModal === "inside" ? agencyArea?.data : agencyArea)?.map(
        (item: any) => {
          const isSelectable = item.selectable !== false;
          const areaId = item.area_id ?? item.id;
          const areaName =
            (item.area_name ?? item.name)?.replaceAll(">", "،") || "";

          return (
            <React.Fragment key={areaId}>
              <div
                className={
                  "flex items-center justify-between py-2 px-1 rounded-lg transition-colors " +
                  (isSelectable ? "hover:bg-gray-50 cursor-pointer" : "")
                }
              >
                <div className="flex items-center gap-3 flex-1">
                  <input
                    type="radio"
                    checked={selectedRadioId?.area_id === areaId}
                    disabled={!isSelectable}
                    onChange={() => handleCoverageAreaId?.(item)}
                    onClick={(e) => e.stopPropagation()}
                    className={
                      "w-5 h-5 accent-indigo-600 " +
                      (!isSelectable ? "opacity-40 cursor-not-allowed" : "")
                    }
                  />
                  <span className="text-gray-800">{areaName}</span>
                </div>

                <IconButton
                  variant="bare"
                  ariaLabel="مشاهده زیرمجموعه"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRadioOnClick?.(item);
                  }}
                  className="p-1 text-gray-500 hover:text-gray-800"
                >
                  <ChevronLeft size={24} />
                </IconButton>
              </div>
              <hr className="border-gray-200 last:hidden" />
            </React.Fragment>
          );
        }
      )}
    </div>
  );

  const renderStepByStepView = () => (
    <div className="flex flex-col gap-1">
      <h3 className="text-lg font-bold mb-4 mt-2">{uniqueLevelName}</h3>

      {areaDetailsLoading && <LoadingSkeleton />}
      {!areaDetailsLoading && <TotalIsZero query={areaDetails?.length} />}

      {!areaDetailsLoading &&
        areaDetails?.map((item: any) => (
          <React.Fragment key={item.area_id}>
            <div
              className={
                "flex items-center justify-between py-2 px-1 rounded-lg transition-colors " +
                (item.selectable ? "hover:bg-gray-50 cursor-pointer" : "")
              }
            >
              <div className="flex items-center gap-3 flex-1">
                <input
                  type="radio"
                  checked={selectedRadioId?.area_id === item.area_id}
                  disabled={!item.selectable}
                  onChange={() => handleCoverageAreaId?.(item)}
                  onClick={(e) => e.stopPropagation()}
                  className={
                    "w-5 h-5 accent-indigo-600 " +
                    (!item.selectable ? "opacity-40 cursor-not-allowed" : "")
                  }
                />
                <span className="text-gray-800">{item.name}</span>
              </div>

              {item.children?.length > 0 && (
                <IconButton
                  variant="bare"
                  ariaLabel="مشاهده زیرمجموعه"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRadioOnClick?.(item);
                  }}
                  className="p-1 text-gray-500 hover:text-gray-800"
                >
                  <ChevronLeft size={24} />
                </IconButton>
              )}
            </div>
            <hr className="border-gray-200 last:hidden" />
          </React.Fragment>
        ))}
    </div>
  );

  return (
    <>
      <Modal
        isOpen={open}
        onClose={handleClose}
        title="انتخاب منطقه تحت پوشش"
        className="w-[92%] max-w-200 h-[92vh] max-h-200 !flex !flex-col overflow-hidden lg:!max-w-200"
        animate={false}
      >
        <div className="flex flex-col h-full min-h-0 -mx-4 sm:-mx-8 -mb-8">
          <p className="px-4 sm:px-8 py-2 text-gray-600 text-base shrink-0">
            منطقه تحت پوشش مورد نظر خود را انتخاب کنید.
          </p>

          <div className="flex-1 px-4 sm:px-8 overflow-y-auto pb-4 min-h-0">
            {stepCounter === 0
              ? renderTreeViewFirstStep()
              : renderStepByStepView()}
          </div>

          <div className="px-4 sm:px-8 py-5 border-t border-gray-200 flex items-center justify-between bg-gray-50 shrink-0">
            <div className="flex flex-row justify-between gap-3 w-full">
              <div>
                {stepCounter !== 0 && (
                  <Button
                    text="مرحله قبل"
                    onClick={() => handleBack?.("modal")}
                    disabled={stepCounter === 0}
                    iconName="lucide:arrow-right"
                    className="px-6 py-2.5 text-sm font-medium text-indigo-700 bg-white border border-indigo-600 rounded-[50px] hover:bg-indigo-50 transition-colors"
                  />
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  text="انصراف"
                  onClick={handleClose}
                  className="px-12 py-4 text-sm font-medium text-indigo-700 bg-white border border-indigo-600 rounded-[50px] hover:bg-indigo-50 transition-colors flex items-center justify-center"
                />
                <Button
                  text="انتخاب منطقه تحت پوشش"
                  disabled={
                    !selectedRadioId ||
                    (areaDetails?.length === 0 && stepCounter > 0) ||
                    (typeModal === "inside" && stepCounter === 0)
                  }
                  onClick={handleAreaModalOpen}
                  className={
                    "px-2 py-2.5 text-sm font-medium text-white " +
                    "bg-indigo-600 border border-indigo-600 rounded-[50px] " +
                    "hover:bg-indigo-700 active:bg-indigo-800 transition-colors " +
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmArea
        open={areaModalOpen}
        onClose={handleAreaModalClose}
        onConfirm={() => {
          handleAreaModalClose();
          onClose?.(null);
        }}
        pathData={pathData}
        agencyAreaAdd={true}
      />
    </>
  );
}
